// ═══════════════════════════════════════════════════════════════════════
// Temps › Journées (DRH, RH) — journées interprétées filtrées par catégorie (conception § 7.12,
// § 14.5) : normales, congés, permissions, missions, formations, maladie (agrégée, sans nom ni
// diagnostic), retards, anomalies, absences qualifiées, heures supplémentaires, régularisations
// tardives. Dans le périmètre de la personne ; pagination par clé ; détail d'une journée au clic.
// Les filtres sont dans l'adresse (liens depuis le tableau de bord et la clôture).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, HeartPulse, Info } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Badge, BandeauInfo, EtatVide, Squelette, Selecteur } from '../../design/composants';
import { NavTemps, useDroitsTemps, AccesTemps, lireRh, messageErreur, heures, jourCourt, nomPersonne, aujourdhui, PagesParCle } from './rhCommun';
// Détail d'une journée : composant partagé de l'agent H, lu par l'API RH, décisions par la boîte RH.
import JourneeDetailModal from './JourneeDetailModal';

const TON_LIBELLE = (l) => (['Pointage incomplet', 'À qualifier', 'À vérifier', 'Présence à confirmer', 'Absence non justifiée'].includes(l) ? 'alerte'
  : ['Normal'].includes(l) ? 'succes' : l === 'Retard' ? 'alerte' : 'neutre');

export default function RhJourneesPage() {
  const droits = useDroitsTemps();
  const [params, setParams] = useSearchParams();
  const filtre = params.get('filtre') || 'anomalies';
  const site = params.get('site') || '';
  const du = params.get('du') || `${aujourdhui().slice(0, 7)}-01`;
  const au = params.get('au') || aujourdhui();
  const [pages, setPages] = useState([null]); // curseurs des pages visitées
  const [rangPage, setRangPage] = useState(0);
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [detail, setDetail] = useState(null);

  const maj = (k, v) => {
    const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k);
    setPages([null]); setRangPage(0); setParams(p, { replace: true });
  };
  const charger = useCallback(async () => {
    setErreur(''); setDonnees(null);
    try { setDonnees(await lireRh('/journees', { filtre, site: site || undefined, du, au, page: pages[rangPage] || undefined })); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [filtre, site, du, au, pages, rangPage]);
  useEffect(() => { if (droits?.rh?.menu?.journees) charger(); }, [droits, charger]);

  const filtres = droits?.rh?.filtres || [];
  const filtreCourant = filtres.find((f) => f.code === filtre);
  const suivante = () => { if (!donnees?.page_suivante) return; setPages([...pages.slice(0, rangPage + 1), donnees.page_suivante]); setRangPage(rangPage + 1); };
  const precedente = () => setRangPage(Math.max(0, rangPage - 1));
  const tailleParPage = 50;

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Journées' }]} titre="Journées"
          sousTitre="Choisissez une catégorie : la liste montre les journées interprétées correspondantes, dans votre périmètre." />
        <NavTemps actif="journees" />
        <AccesTemps droits={droits} rubrique="journees">
          <div className="rh-vues rh-vues-ligne" role="group" aria-label="Catégorie de journées">
            {filtres.map((f) => (
              <button key={f.code} type="button" className="rh-vue" aria-pressed={filtre === f.code} onClick={() => maj('filtre', f.code)} title={f.aide || undefined}>
                {f.agrege && <HeartPulse size={13} aria-hidden="true" />}{f.libelle}
              </button>
            ))}
          </div>
          <div className="rh-outils">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>Du
              <input type="date" className="rh-champ-saisie" value={du} max={au} onChange={(e) => e.target.value && maj('du', e.target.value)} /></label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>au
              <input type="date" className="rh-champ-saisie" value={au} min={du} onChange={(e) => e.target.value && maj('au', e.target.value)} /></label>
            {droits?.rh?.sites?.length > 1 && (
              <div style={{ minWidth: 220 }}><Selecteur libelle="Site" valeur={site} onChange={(v) => maj('site', v)} options={droits.rh.sites.map((s) => ({ valeur: s.id, libelle: s.nom }))} /></div>
            )}
          </div>
          {filtreCourant?.aide && <p className="rh-form-aide" style={{ margin: '0 0 10px', display: 'flex', gap: 6, alignItems: 'center' }}><Info size={14} aria-hidden="true" />{filtreCourant.aide}</p>}
          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
          {!donnees && !erreur && <Squelette largeur="60%" />}

          {donnees?.agrege && (
            <Carte titre="Maladie — chiffres agrégés" Icone={HeartPulse}>
              <p className="rh-form-aide" style={{ margin: '0 0 10px' }}>
                Aucun nom ni diagnostic. En dessous de {donnees.seuil} personnes, le chiffre est masqué pour protéger la vie privée.
                Le suivi nominatif des arrêts est réservé à la RH habilitée (Arrêts et certificats).
              </p>
              {!donnees.lignes.length ? <EtatVide titre="Aucune journée de maladie sur la période" /> : (
                <div className="rh-conteneur-tableau">
                  <table className="rh-tableau">
                    <thead><tr><th>Site</th><th>Personnes</th><th>Journées</th></tr></thead>
                    <tbody>
                      {donnees.lignes.map((l) => (
                        <tr key={l.site_id || 'aucun'}>
                          <td>{l.site_nom || 'Sans site'}</td>
                          {l.masque ? <td colSpan={2} className="rh-cellule-secondaire">Moins de {donnees.seuil} personnes : chiffre masqué</td>
                            : <><td>{l.nb_salaries}</td><td>{l.nb_journees}</td></>}
                        </tr>
                      ))}
                      <tr>
                        <td><strong style={{ fontWeight: 500 }}>Total</strong></td>
                        {donnees.total.masque || donnees.total.nb_salaries === null
                          ? <td colSpan={2} className="rh-cellule-secondaire">{donnees.total.masque ? `Moins de ${donnees.seuil} personnes : chiffre masqué` : 'Aucune'}</td>
                          : <><td>{donnees.total.nb_salaries}</td><td>{donnees.total.nb_journees}</td></>}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Carte>
          )}

          {donnees && !donnees.agrege && (
            <Carte titre={`${filtreCourant?.libelle || 'Journées'} · ${donnees.total} journée${donnees.total > 1 ? 's' : ''}`} Icone={CalendarDays}>
              {!donnees.lignes.length ? <EtatVide Icone={CalendarDays} titre="Aucune journée dans cette catégorie" texte="Changez de période ou de catégorie." /> : (
                <>
                  <div className="rh-conteneur-tableau">
                    <table className="rh-tableau">
                      <thead>
                        <tr><th>Date</th><th>Salarié</th><th>Journée</th><th>Travaillé</th><th>Retard</th><th>Heures sup.</th><th>Points à régler</th></tr>
                      </thead>
                      <tbody>
                        {donnees.lignes.map((l) => (
                          <tr key={l.id} className="rh-ligne" tabIndex={0} style={{ cursor: 'pointer' }}
                            onClick={() => setDetail(l)} onKeyDown={(e) => { if (e.key === 'Enter') setDetail(l); }}>
                            <td style={{ whiteSpace: 'nowrap' }}>{jourCourt(l.date)}</td>
                            <td><span className="rh-cellule-principale">{nomPersonne(l.employe)}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.employe.matricule}{l.site_nom ? ` · ${l.site_nom}` : ''}</span></td>
                            <td><Badge ton={TON_LIBELLE(l.libelle)}>{l.libelle}</Badge>{l.regularisee && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Régularisée sur un mois suivant</span>}</td>
                            <td>{l.min_travaillees === null ? '' : heures(l.min_travaillees)}</td>
                            <td>{l.min_retard ? `${l.min_retard} min` : ''}</td>
                            <td>{l.hs_validees_min ? `${heures(l.hs_validees_min)} validées` : l.min_excedent ? `${heures(l.min_excedent)} à valider` : ''}</td>
                            <td>{l.anomalies.map((a) => a.libelle).join(' · ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PagesParCle total={donnees.total} rang={rangPage * tailleParPage} nbPage={donnees.lignes.length} aSuivante={!!donnees.page_suivante}
                    onPrecedente={precedente} onSuivante={suivante} />
                  <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>Retard : au-delà de la tolérance. Heures sup. : dépassement de l'horaire, validé ou en attente de validation. Cliquez sur une ligne pour le détail.</p>
                </>
              )}
            </Carte>
          )}
          {detail && (
            <JourneeDetailModal employeId={detail.employe.id} date={detail.date} url={`/api/temps/rh/journees/${detail.employe.id}/${detail.date}`}
              apiDecision="/api/temps/rh" onFermer={() => setDetail(null)} onModifie={() => charger()} />
          )}
        </AccesTemps>
      </div>
    </Layout>
  );
}
